// frontend/src/components/modals/ContaModal.jsx

import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, Row, Col, InputNumber, message } from 'antd';
import { useMediaQuery } from 'react-responsive';
import { createConta, updateConta } from '../../services/accountService';
import { getBancos } from '../../services/bancoService';
import { getErrorMessage } from '../../utils/errorHandler';
import MobileLaunchSheet from '../MobileLaunchSheet/MobileLaunchSheet';

const { Option } = Select;

const ContaModal = ({ isOpen, onClose, onSave, contaToEdit }) => {
    const isMobile = useMediaQuery({ maxWidth: 768 });
    const [form] = Form.useForm();
    const [bancosFiltrados, setBancosFiltrados] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Hook de mensagem interno
    const [messageApi, contextHolder] = message.useMessage();

    const isEditMode = !!contaToEdit;

    useEffect(() => {
        if (isOpen) {
            // 1. Setup imediato (Síncrono)
            form.resetFields();

            if (isEditMode && contaToEdit) {
                form.setFieldsValue({
                    ...contaToEdit,
                    banco_id: contaToEdit.banco?.id
                });

                // Pré-popula o banco atual se ele existir, para o select não ficar em branco enquanto carrega
                if (contaToEdit.banco && typeof contaToEdit.banco === 'object') {
                    setBancosFiltrados(prevBancos => {
                        const exists = prevBancos.some(b => b.id === contaToEdit.banco.id);
                        return exists ? prevBancos : [...prevBancos, contaToEdit.banco];
                    });
                }
            } else {
                form.setFieldsValue({
                    tipo_conta: 'conta_corrente',
                    saldo_inicial: 0
                });
            }

            // 2. Busca lista completa em background
            const carregarBancos = async () => {
                setIsLoading(true);
                try {
                    const todosOsBancos = await getBancos();

                    // ALTERAÇÃO: Removemos a filtragem por slugsDosLogosExistentes.
                    // Agora confiamos que o backend retorna os bancos válidos.
                    const bancosOrdenados = todosOsBancos.sort((a, b) => a.nome.localeCompare(b.nome));

                    setBancosFiltrados(bancosOrdenados);
                } catch (err) {
                    console.error("Falha ao carregar bancos do backend", err);
                    // 2. USO NO FETCH DE BANCOS
                    messageApi.error(getErrorMessage(err, 'Não foi possível carregar a lista de instituições financeiras.'));
                } finally {
                    setIsLoading(false);
                }
            };

            carregarBancos();
        }
    }, [isOpen, contaToEdit, form, isEditMode, messageApi]);

    const handleFormSubmit = async (closeOnSuccess = true) => {
        try {
            const values = await form.validateFields();
            setIsLoading(true);

            const dataToSubmit = { ...values, banco_id: values.banco_id || null };
            let successMsg = '';

            if (isEditMode) {
                await updateConta(contaToEdit.id, dataToSubmit);
                successMsg = 'Conta atualizada com sucesso!';
            } else {
                await createConta(dataToSubmit);
                successMsg = 'Conta criada com sucesso!';
            }

            // Envia para o pai (ContasPage) exibir ou gerenciar
            onSave(closeOnSuccess, successMsg);

            if (!closeOnSuccess) {
                const currentType = values.tipo_conta;
                form.resetFields();
                form.setFieldsValue({
                    tipo_conta: currentType, // Mantém o tipo para agilizar
                    saldo_inicial: 0
                });
            }
        } catch (err) {
            console.error("Falha ao salvar conta", err);
            // 3. USO NO SALVAR (CREATE/UPDATE)
            messageApi.error(getErrorMessage(err, "Erro ao salvar conta."));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = () => handleFormSubmit(true);
    const handleSubmitAndAddNew = () => handleFormSubmit(false);

    const handleCancel = () => { !isLoading && onClose(); };

    const filterOption = (input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase());

    const formContent = (
            <Form form={form} layout="vertical" size={isMobile ? 'large' : 'middle'}>
                <Form.Item name="nome" label="Nome da Conta" rules={[{ required: true, message: 'O nome é obrigatório' }]}>
                    <Input placeholder="Ex: Conta Principal, Carteira" />
                </Form.Item>

                <Row gutter={16}>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="tipo_conta" label="Tipo de Conta" rules={[{ required: true, message: 'Selecione o tipo' }]}>
                            <Select>
                                <Option value="conta_corrente">Conta Corrente</Option>
                                <Option value="poupanca">Poupança</Option>
                                <Option value="investimento">Investimento</Option>
                                <Option value="carteira">Carteira</Option>
                                <Option value="outros">Outros</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="saldo_inicial" label="Saldo Inicial (R$)" rules={[{ required: true, message: 'O saldo é obrigatório' }]}>
                            <InputNumber
                                style={{ width: '100%' }}
                                precision={2}
                                step={100}
                                formatter={(value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)}
                                parser={(value) => {
                                    if (!value) return '';
                                    const onlyNumbers = value.replace(/[^\d]/g, '');
                                    if (!onlyNumbers) return '';
                                    const num = parseInt(onlyNumbers, 10);
                                    return num / 100;
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item name="banco_id" label="Instituição Financeira (Opcional)">
                    <Select showSearch placeholder="Nenhuma / Outra" allowClear loading={isLoading} optionFilterProp="children" filterOption={filterOption}>
                        <Option value={null}>Nenhuma / Outra</Option>
                        {bancosFiltrados.map(banco => (
                            <Option key={banco.id} value={banco.id}>{banco.nome}</Option>
                        ))}
                    </Select>
                </Form.Item>
            </Form>
    );

    if (isMobile) {
        return (
            <>
                {contextHolder}
                <MobileLaunchSheet
                    open={isOpen}
                    title={isEditMode ? 'Editar Conta' : 'Nova Conta'}
                    onCancel={handleCancel}
                    onSave={handleSubmit}
                    saveLoading={isLoading}
                    cancelDisabled={isLoading}
                    secondaryAction={
                        !isEditMode ? (
                            <Button type="default" loading={isLoading} onClick={handleSubmitAndAddNew} block size="large">
                                Salvar e Adicionar Outra
                            </Button>
                        ) : null
                    }
                >
                    {formContent}
                </MobileLaunchSheet>
            </>
        );
    }

    return (
        <Modal
            open={isOpen}
            title={isEditMode ? 'Editar Conta' : 'Adicionar Nova Conta'}
            onCancel={handleCancel}
            destroyOnClose
            footer={[
                <Button key="back" onClick={handleCancel} disabled={isLoading}>Cancelar</Button>,
                !isEditMode && (
                    <Button key="saveAndNew" onClick={handleSubmitAndAddNew} disabled={isLoading}>
                        Salvar e Adicionar Outra
                    </Button>
                ),
                <Button key="submit" type="primary" loading={isLoading} onClick={handleSubmit}>Salvar</Button>,
            ]}
        >
            {contextHolder}
            {formContent}
        </Modal>
    );
};

export default ContaModal;